import type { Filter } from 'Types/filter';
import type { Make } from 'Types/make';
import type { Model } from 'Types/model';
import { formatText } from 'Utilities/formatters';

import type {
	CheckboxChipConfig,
	QuickFilterId,
	RangeChipConfig,
	SingleValueRangeChipConfig,
} from './FilterBarV2.consts';
import {
	CHECKBOX_OPTIONS_MAP,
	LISTING_ATTRIBUTE_OPTIONS,
	SELLER_TYPE_OPTIONS,
	VEHICLE_TYPE_OPTIONS,
} from './FilterBarV2.consts';

const formatCompact = (value: number): string => {
	if (value >= 1000 && value % 1000 === 0) {
		return `${value / 1000}K`;
	}
	if (value >= 1000) {
		const k = value / 1000;
		const rounded = Math.round(k * 10) / 10;
		return `${rounded}K`;
	}
	return `${value}`;
};

export const getPriceLabel = (filters: Filter): string | undefined => {
	const { displayPriceFrom, displayPriceTo } = filters;
	if (!displayPriceFrom && !displayPriceTo) {
		return undefined;
	}
	const from = displayPriceFrom ? `£${formatCompact(displayPriceFrom)}` : '£0';
	const to = displayPriceTo ? `£${formatCompact(displayPriceTo)}` : '£100K+';
	return `${from} - ${to}`;
};

export const getMileageLabel = (filters: Filter): string | undefined => {
	const { mileageFrom, mileageTo } = filters;
	if (!mileageFrom && !mileageTo) {
		return undefined;
	}
	const from = mileageFrom ? formatCompact(mileageFrom) : '0';
	const to = mileageTo ? formatCompact(mileageTo) : '150K+';
	return `${from} - ${to} mi`;
};

export const getAgeLabel = (filters: Filter): string | undefined => {
	const { ageFrom, ageTo } = filters;
	if (!ageFrom && !ageTo) {
		return undefined;
	}
	const from = ageFrom ? `${ageFrom}` : '0';
	const to = ageTo ? `${ageTo}` : '20+';
	return `${from} - ${to} years`;
};

export const getDistanceLabel = (filters: Filter): string | undefined => {
	const { maxDistance } = filters;
	if (!maxDistance) {
		return undefined;
	}
	return `0 - ${formatCompact(maxDistance)} mi away`;
};

// Derived from VEHICLE_TYPE_OPTIONS so label changes only need to happen in one place
const VEHICLE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
	VEHICLE_TYPE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

// Derived from SELLER_TYPE_OPTIONS so label changes only need to happen in one place
const SELLER_TYPE_LABELS: Record<string, string> = Object.fromEntries(
	SELLER_TYPE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

interface GetActiveLabelParams {
	filterId: QuickFilterId;
	filters: Filter;
	makes?: Make[];
	models?: Model[];
}

export const getActiveLabel = ({ filterId, filters, makes, models }: GetActiveLabelParams): string | undefined => {
	switch (filterId) {
		case 'price':
			return getPriceLabel(filters);
		case 'mileage':
			return getMileageLabel(filters);
		case 'age':
			return getAgeLabel(filters);
		case 'distance':
			return getDistanceLabel(filters);
		case 'makeModel':
			if (filters.make.length === 0 && (!models || models.length === 0)) return undefined;
			{
				const makeNameMap = new Map(makes?.map((m) => [m.slug, m]) || []);
				const parts: string[] = [];

				for (const slug of filters.make) {
					const make = makeNameMap.get(slug);
					const makeName = make?.name || slug;
					const makeModels = models?.filter((m) => make && m.makeId === make.id) || [];

					if (makeModels.length > 0) {
						parts.push(`${makeName} (${makeModels.map((m) => m.namePretty || m.name).join(', ')})`);
					} else {
						parts.push(makeName);
					}
				}

				return parts.length > 0 ? parts.join(', ') : undefined;
			}
		case 'vehicleType':
			return filters.vehicleClass.length > 0
				? filters.vehicleClass.map((v) => VEHICLE_TYPE_LABELS[v] || v).join(', ')
				: undefined;
		case 'sellerType':
			return filters.sellerType.length > 0
				? filters.sellerType.map((v) => SELLER_TYPE_LABELS[v] || v).join(', ')
				: undefined;
		case 'listingAttributes': {
			const active = LISTING_ATTRIBUTE_OPTIONS.filter(({ value }) => value && filters[value as keyof Filter]).map(
				({ label }) => label,
			);
			return active.length > 0 ? active.join(', ') : undefined;
		}
		default:
			return undefined;
	}
};

const countFilters = (filters: Filter, excludeKeys: Set<string>): number => {
	let count = 0;
	for (const [key, value] of Object.entries(filters)) {
		if (excludeKeys.has(key)) {
			continue;
		}
		if (Array.isArray(value) && value.length > 0) {
			count += value.length;
		} else if (typeof value === 'boolean' && value) {
			count++;
		} else if (typeof value === 'number') {
			count++;
		}
	}
	return count;
};

export const getActiveFilterCount = (filters: Filter): number => {
	const excludeKeys = new Set([
		'includeOnlyInternalTestingVehicles',
		'includeSold',
		'includeUnderOffer',
		'order',
		'selectedVehicle',
		'sort',
	]);
	return countFilters(filters, excludeKeys);
};

const BAR_FILTER_KEYS = new Set([
	'ageFrom',
	'ageTo',
	'displayPriceFrom',
	'displayPriceTo',
	'isPaidListing',
	'hasEarlyDocuments',
	'make',
	'maxDistance',
	'mileageFrom',
	'mileageTo',
	'model',
	'sellerType',
	'vehicleClass',
]);

export const getModalOnlyFilterCount = (filters: Filter): number => {
	// TODO: once the seen filter is moved into the filter store, include it here
	// and remove the special-casing in AllFiltersContent's isSectionDirty toggle branch.
	const excludeKeys = new Set([
		...BAR_FILTER_KEYS,
		'includeOnlyInternalTestingVehicles',
		'includeSold',
		'includeUnderOffer',
		'order',
		'selectedVehicle',
		'sort',
	]);
	return countFilters(filters, excludeKeys);
};

export type RangeUnit = 'distance' | 'miles' | 'owners' | 'price' | 'years';

interface FormatRangeValueParams {
	from: Nullable<number>;
	max: number;
	min: number;
	to: Nullable<number>;
	unit: RangeUnit;
}

export const formatRangeValue = ({ from, max, min, to, unit }: FormatRangeValueParams): string => {
	const fromVal = from ?? min;
	const toVal = to ?? max;
	const isAtMax = toVal >= max;

	switch (unit) {
		case 'price':
			return `£${formatText(fromVal)} - £${formatText(isAtMax ? max : toVal)}${isAtMax ? '+' : ''}`;
		case 'miles':
			return `${formatText(fromVal)} - ${formatText(isAtMax ? max : toVal)}${isAtMax ? '+' : ''} mi`;
		case 'years':
			return `${fromVal} - ${isAtMax ? max : toVal}${isAtMax ? '+' : ''} years`;
		case 'owners':
			return `${fromVal} - ${isAtMax ? max : toVal}${isAtMax ? '+' : ''} owners`;
		case 'distance':
			return `${formatText(fromVal)} - ${formatText(isAtMax ? max : toVal)}${isAtMax ? '+' : ''} mi away`;
		default:
			return `${formatText(fromVal)} - ${formatText(isAtMax ? max : toVal)}`;
	}
};

interface GetFilterValueParams {
	filters: Filter;
	key: string;
}

export const getFilterNumericValue = ({ filters, key }: GetFilterValueParams): Nullable<number> => {
	const value = filters[key as keyof Filter];
	return typeof value === 'number' ? value : null;
};

export const getFilterBooleanValue = ({ filters, key }: GetFilterValueParams): boolean => {
	const value = filters[key as keyof Filter];
	return typeof value === 'boolean' ? value : false;
};

export const getFilterListValue = ({ filters, key }: GetFilterValueParams): string[] => {
	const value = filters[key as keyof Filter];
	return Array.isArray(value) ? (value as string[]) : [];
};

export type SetValue = (args: { id: string; value: unknown }) => void;
export type UnsetValue = (args: { id: string }) => void;

export type SelectionKind = 'array' | 'boolean' | 'independentBooleans';

export interface SelectionSpec {
	activeValue?: string;
	filterId: string;
	invertSelection?: boolean;
	kind: SelectionKind;
	options: ReadonlyArray<{ value: string }>;
}

export interface RangeSpec {
	fromId?: string;
	toId?: string;
}

export const getSelectionValue = (selection: SelectionSpec, filters: Filter): string[] => {
	if (selection.kind === 'independentBooleans') {
		return selection.options
			.filter((option) => option.value && getFilterBooleanValue({ filters, key: option.value }))
			.map((option) => option.value);
	}
	if (selection.kind === 'boolean') {
		const storedValue = getFilterBooleanValue({ filters, key: selection.filterId });
		const isActive = selection.invertSelection ? !storedValue : storedValue;
		return isActive && selection.activeValue ? [selection.activeValue] : [];
	}
	return getFilterListValue({ filters, key: selection.filterId });
};

export const applySelection = (selection: SelectionSpec, selected: string[], setValue: SetValue): void => {
	if (selection.kind === 'independentBooleans') {
		for (const option of selection.options) {
			if (option.value) setValue({ id: option.value, value: selected.includes(option.value) });
		}
	} else if (selection.kind === 'boolean') {
		const isActive = selection.activeValue ? selected.includes(selection.activeValue) : false;
		setValue({ id: selection.filterId, value: selection.invertSelection ? !isActive : isActive });
	} else {
		setValue({ id: selection.filterId, value: selected });
	}
};

export const clearSelection = (selection: SelectionSpec, setValue: SetValue): void => {
	if (selection.kind === 'independentBooleans') {
		for (const option of selection.options) {
			if (option.value) setValue({ id: option.value, value: false });
		}
	} else if (selection.kind === 'boolean') {
		setValue({ id: selection.filterId, value: !!selection.invertSelection });
	} else {
		setValue({ id: selection.filterId, value: [] });
	}
};

export const applyRange = (
	range: RangeSpec,
	from: Nullable<number>,
	to: Nullable<number>,
	setValue: SetValue,
	unsetValue: UnsetValue,
): void => {
	if (range.fromId) {
		if (from !== null) setValue({ id: range.fromId, value: from });
		else unsetValue({ id: range.fromId });
	}
	if (range.toId) {
		if (to !== null) setValue({ id: range.toId, value: to });
		else unsetValue({ id: range.toId });
	}
};

export const clearRange = (range: RangeSpec, unsetValue: UnsetValue): void => {
	if (range.fromId) unsetValue({ id: range.fromId });
	if (range.toId) unsetValue({ id: range.toId });
};

const checkboxToSelectionSpec = (
	config: CheckboxChipConfig,
	optionsMap: Record<string, ReadonlyArray<{ value: string }>> = CHECKBOX_OPTIONS_MAP,
): SelectionSpec => ({
	activeValue: config.type === 'boolean' ? config.activeValue : undefined,
	filterId: config.filterId,
	invertSelection: config.type === 'boolean' ? config.invertSelection : undefined,
	kind: config.type,
	options: optionsMap[config.filterId] ?? [],
});

const rangeChipToSpec = (config: RangeChipConfig | SingleValueRangeChipConfig): RangeSpec => ({
	fromId: config.type === 'range' ? config.fromId : undefined,
	toId: config.toId,
});

interface GetCheckboxSelectedParams {
	config: CheckboxChipConfig;
	filters: Filter;
	optionsMap: Record<string, { value: string }[]>;
}

export const getCheckboxSelected = ({ config, filters, optionsMap }: GetCheckboxSelectedParams): string[] =>
	getSelectionValue(checkboxToSelectionSpec(config, optionsMap), filters);

interface ApplyRangeFilterParams {
	config: RangeChipConfig | SingleValueRangeChipConfig;
	from: Nullable<number>;
	setValue: SetValue;
	to: Nullable<number>;
	unsetValue: UnsetValue;
}

export const applyRangeFilter = ({ config, from, setValue, to, unsetValue }: ApplyRangeFilterParams): void =>
	applyRange(rangeChipToSpec(config), from, to, setValue, unsetValue);

interface ClearRangeFilterParams {
	config: RangeChipConfig | SingleValueRangeChipConfig;
	unsetValue: UnsetValue;
}

export const clearRangeFilter = ({ config, unsetValue }: ClearRangeFilterParams): void =>
	clearRange(rangeChipToSpec(config), unsetValue);

interface ApplyCheckboxFilterParams {
	config: CheckboxChipConfig;
	selected: string[];
	setValue: SetValue;
}

export const applyCheckboxFilter = ({ config, selected, setValue }: ApplyCheckboxFilterParams): void =>
	applySelection(checkboxToSelectionSpec(config), selected, setValue);

interface ClearCheckboxFilterParams {
	config: CheckboxChipConfig;
	setValue: SetValue;
}

export const clearCheckboxFilter = ({ config, setValue }: ClearCheckboxFilterParams): void =>
	clearSelection(checkboxToSelectionSpec(config), setValue);
